import { Link, useNavigate,useLocation  } from 'react-router-dom';
import { isLoggedIn, removeToken, isAdmin } from '../utils/tokenUtils';
import { useCart } from '../context/CartContext';
import { useState, useEffect, useRef } from 'react';
import { getActiveNoticeBanners } from '../api/noticeBannerApi';
import { getActiveNavMenus } from '../api/navMenuApi';
import styles from './Header.module.css';
import { getSettings } from '../api/settingsApi';

function Header() {
    const navigate = useNavigate();
    const loggedIn = isLoggedIn();
    const admin = isAdmin();
    const { cartCount, refreshCartCount } = useCart();
    const [noticeBanners, setNoticeBanners] = useState([]);
    const [currentNotice, setCurrentNotice] = useState(0);
    const [fade, setFade] = useState(true);
    const [navMenus, setNavMenus] = useState([]);
    const intervalRef = useRef(null);
    const [shopName, setShopName] = useState('SHOP');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [expandedMenu, setExpandedMenu] = useState(null);
    const location = useLocation();

    useEffect(() => {
        getSettings().then(res => setShopName(res.data.shop_name || 'SHOP')).catch(() => {});
        getActiveNoticeBanners().then(res => setNoticeBanners(res.data)).catch(() => {});
        getActiveNavMenus().then(res => setNavMenus(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (noticeBanners.length <= 1) return;
        intervalRef.current = setInterval(() => {
            setFade(false);
            setTimeout(() => {
                setCurrentNotice(i => (i + 1) % noticeBanners.length);
                setFade(true);
            }, 400);
        }, 3000);
        return () => clearInterval(intervalRef.current);
    }, [noticeBanners]);

    // 모바일 메뉴 열릴 때 스크롤 방지
    useEffect(() => {
        document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const handleNoticeClick = (banner) => {
        if (!banner || banner.linkType === 'NONE') return;
        if (banner.linkType === 'EVENT') navigate(`/events/${banner.linkValue}`);
        else if (banner.linkType === 'PRODUCT') navigate(`/products/${banner.linkValue}`);
        else if (banner.linkType === 'URL') window.open(banner.linkValue, '_blank');
    };

    const handleDotClick = (e, i) => {
        e.stopPropagation();
        clearInterval(intervalRef.current);
        setFade(false);
        setTimeout(() => { setCurrentNotice(i); setFade(true); }, 400);
    };

    const handleMenuClick = (menu) => {
        setMobileMenuOpen(false);
        if (menu.linkType === 'URL') navigate(menu.linkValue);
        else if (menu.linkType === 'CATEGORY') {
            if (menu.linkValue === 'ALL' || !menu.linkValue) navigate('/products');
            else navigate(`/products?category=${menu.linkValue}`);
        }
    };

    const handleLogout = () => {
        removeToken();
        refreshCartCount();
        setMobileMenuOpen(false);
        navigate('/login');
    };

    const currentBanner = noticeBanners[currentNotice];

    return (
        <header className={styles.header}>
            {noticeBanners.length > 0 && (
                <div
                    className={styles.banner}
                    style={{
                        backgroundColor: currentBanner?.bgColor || '#111',
                        color: currentBanner?.textColor || '#fff',
                        cursor: currentBanner?.linkType !== 'NONE' ? 'pointer' : 'default',
                        position: 'relative'
                    }}
                    onClick={() => handleNoticeClick(currentBanner)}
                >
                    <span style={{ opacity: fade ? 1 : 0, transition: 'opacity 0.4s ease', display: 'inline-block' }}>
                        {currentBanner?.content}
                    </span>
                    {noticeBanners.length > 1 && (
                        <div className={styles.noticeDots}>
                            {noticeBanners.map((_, i) => (
                                <span key={i}
                                    className={`${styles.noticeDot} ${i === currentNotice ? styles.noticeDotActive : ''}`}
                                    onClick={e => handleDotClick(e, i)} />
                            ))}
                        </div>
                    )}
                </div>
            )}
            <div className={styles.container}>
                <Link to="/" className={styles.logo}>{shopName}</Link>

                {/* 데스크탑 네비 */}
                <nav className={styles.nav}>
                    {navMenus.map(menu => (
                        <div key={menu.navMenuId} className={styles.navItem}>
                            <span className={styles.navLink} onClick={() => handleMenuClick(menu)}>
                                {menu.label}
                            </span>
                            {menu.children?.length > 0 && (
                                <div className={styles.dropdown}>
                                    <div className={styles.dropdownInner}>
                                        <Link to={`/products?category=${menu.categoryId}`}
                                            className={`${styles.dropdownChild} ${styles.dropdownAllLink}`}>
                                            전체보기
                                        </Link>
                                        <div className={styles.dropdownDividerH} />
                                        {menu.children.map(child => (
                                            <Link key={child.categoryId}
                                                to={`/products?category=${child.categoryId}`}
                                                className={styles.dropdownChild}>
                                                {child.name}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                {/* 데스크탑 우측 메뉴 */}
                <div className={styles.rightMenu}>
                    {loggedIn ? (
                        <>
                            {admin && (
                                <>
                                    <Link to="/admin" className={`${styles.menuItem} ${styles.adminMenu}`}>관리자</Link>
                                    <span className={styles.separator}>|</span>
                                </>
                            )}
                            <Link to="/mypage" className={styles.menuItem}>마이페이지</Link>
                            <span className={styles.separator}>|</span>
                            <Link to="/wishlist" className={styles.menuItem}>찜한 상품</Link>
                            <span className={styles.separator}>|</span>
                            <Link to="/recent" className={styles.menuItem}>최근 본 상품</Link>
                            <span className={styles.separator}>|</span>
                            <span className={styles.menuItem} onClick={handleLogout}>로그아웃</span>
                        </>
                    ) : (
                        <>
                            <Link to="/login" state={{ from: location.pathname }} className={styles.menuItem}>로그인</Link>
                            <span className={styles.separator}>|</span>
                            <Link to="/register" className={styles.menuItem}>회원가입</Link>
                        </>
                    )}
                    <Link to="/cart" className={styles.cartIcon}>
                        🛒
                        {cartCount > 0 && <span className={styles.cartBadge}>{cartCount}</span>}
                    </Link>
                </div>

                {/* 모바일 우측 */}
                <div className={styles.mobileRight}>
                    <Link to="/cart" className={styles.cartIcon}>
                        🛒
                        {cartCount > 0 && <span className={styles.cartBadge}>{cartCount}</span>}
                    </Link>
                    <button className={styles.hamburger} onClick={() => setMobileMenuOpen(prev => !prev)}>
                        {mobileMenuOpen ? '✕' : '☰'}
                    </button>
                </div>
            </div>

            {/* 모바일 메뉴 오버레이 */}
            {mobileMenuOpen && (
                <div className={styles.mobileOverlay} onClick={() => setMobileMenuOpen(false)} />
            )}

            {/* 모바일 슬라이드 메뉴 */}
            <div className={`${styles.mobileMenu} ${mobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
                <div className={styles.mobileMenuHeader}>
                    <span className={styles.mobileLogo}>{shopName}</span>
                    <button className={styles.mobileCloseBtn} onClick={() => setMobileMenuOpen(false)}>✕</button>
                </div>

                {/* 모바일 네비 */}
                <nav className={styles.mobileNav}>
                    {navMenus.map(menu => (
                        <div key={menu.navMenuId}>
                            <div className={styles.mobileNavItem}
                                onClick={() => {
                                    if (menu.children?.length > 0) {
                                        setExpandedMenu(expandedMenu === menu.navMenuId ? null : menu.navMenuId);
                                    } else {
                                        handleMenuClick(menu);
                                    }
                                }}>
                                <span>{menu.label}</span>
                                {menu.children?.length > 0 && (
                                    <span>{expandedMenu === menu.navMenuId ? '▲' : '▼'}</span>
                                )}
                            </div>
                            {menu.children?.length > 0 && expandedMenu === menu.navMenuId && (
                                <div className={styles.mobileSubMenu}>
                                    <Link to={`/products?category=${menu.categoryId}`}
                                        className={styles.mobileSubItem}
                                        onClick={() => setMobileMenuOpen(false)}>
                                        전체보기
                                    </Link>
                                    {menu.children.map(child => (
                                        <Link key={child.categoryId}
                                            to={`/products?category=${child.categoryId}`}
                                            className={styles.mobileSubItem}
                                            onClick={() => setMobileMenuOpen(false)}>
                                            {child.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                <div className={styles.mobileDivider} />

                {/* 모바일 계정 메뉴 */}
                <div className={styles.mobileAccountMenu}>
                    {loggedIn ? (
                        <>
                            {admin && (
                                <Link to="/admin" className={styles.mobileAccountItem}
                                    onClick={() => setMobileMenuOpen(false)}>관리자</Link>
                            )}
                            <Link to="/mypage" className={styles.mobileAccountItem}
                                onClick={() => setMobileMenuOpen(false)}>마이페이지</Link>
                            <Link to="/wishlist" className={styles.mobileAccountItem}
                                onClick={() => setMobileMenuOpen(false)}>찜한 상품</Link>
                            <Link to="/recent" className={styles.mobileAccountItem}
                                onClick={() => setMobileMenuOpen(false)}>최근 본 상품</Link>
                            <span className={styles.mobileAccountItem} onClick={handleLogout}>로그아웃</span>
                        </>
                    ) : (
                        <>
                            <Link to="/login" state={{ from: location.pathname }} className={styles.mobileAccountItem}
                                onClick={() => setMobileMenuOpen(false)}>로그인</Link>
                            <Link to="/register" className={styles.mobileAccountItem}
                                onClick={() => setMobileMenuOpen(false)}>회원가입</Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}

export default Header;